import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  fetchCampaigns as fetchCampaignsAPI,
  fetchDashboard as fetchDashboardAPI,
  fetchDashboardData as fetchDashboardDataAPI,
  saveDashboard as saveDashboardAPI,
} from 'api/DashboardAPI';
import { campaignMediums, campaignStates, defaultCampaignParams } from 'utils/campaigns';
import { formatDateForSave } from 'utils/date';
import { getMetricName } from 'utils/metrics';
import { deepMergeNew, generateId, omitFromObject } from 'utils/helpers';
import { addToRootSaga } from 'store';
import { addWarningToast } from 'modules/toastContainer';
import {
  createDashboardDataObject,
  createWidgetData,
  filterFetchParam,
  formatWidgetDataResponse,
  getWidgetDataType,
  getWidgetDataIdentifier,
  getWidgetFetchParams,
  getWidgetMetric,
  isMetricWidget,
  isCampaignsTableWidget,
} from './utils/dashboardWidgetUtils';
import * as actions from './actions';
import * as actionTypes from './actionTypes';

const getDashboardState = ({ dashboardState: state }) => state;

/**
 * filterFromList
 * Removes an item from a list if an attribute value does not match a given value.
 * @param { Array } list: The list to filter.
 * @param { String } attr: The attribute to test.
 * @param { String } value: The value to test against.
 * @returns { Array } The filtered array.
 */
const filterFromList = (list, attr, value) => list.filter((item) => item[attr] !== value);

/**
 * hasMetricWidgetChanged
 * helper function to determine if a metric widget has changed.
 * @param { Object } oldWidget: The old widget data.
 * @param { Object } widget: The current widget data.
 * @return { Boolean } If the metric widget has changed.
 */
export const hasMetricWidgetChanged = (oldWidget, widget) =>
  isMetricWidget(widget) &&
  (getMetricName(getWidgetMetric(oldWidget)) !== getMetricName(getWidgetMetric(widget)) ||
    getWidgetDataType(oldWidget) !== getWidgetDataType(widget));

/*------------------------------------------------------------------------------------------------*/
/* Fetch Campaign Saga                                                                            */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchCampaigns
 * Saga for fetching campaigns table data
 * @param { Object } params: The campaigns table widget params.
 * @return { void }
 */
export function* fetchCampaigns({ widget }) {
  if (widget.id) {
    const fetchParams = deepMergeNew(defaultCampaignParams, getWidgetFetchParams(widget));
    const stateParams = filterFetchParam(fetchParams, 'stateFilter', campaignStates.ALL.value);
    const mediumParams = filterFetchParam(stateParams, 'mediumFilter', campaignMediums.ALL.value);
    try {
      const { campaignFetchParamData, fetchingCampaignsTableIds } = yield select(getDashboardState);
      const newFetchingCampaignsTableIds = filterFromList(
        fetchingCampaignsTableIds,
        'id',
        widget.id,
      );

      yield put(
        actions.fetchCampaignsInit({
          campaignFetchParamData: {
            ...campaignFetchParamData,
            [widget.id]: fetchParams,
          },
          fetchingCampaignsTableIds: [...fetchingCampaignsTableIds, widget.id],
        }),
      );

      const response = yield call(fetchCampaignsAPI, mediumParams);
      const { dashboardData } = yield select(getDashboardState);
      yield put(
        actions.fetchCampaignsResponse(
          deepMergeNew(
            { dashboardData },
            {
              dashboardData: createDashboardDataObject(widget, response.data),
              fetchingCampaignsTableIds: newFetchingCampaignsTableIds,
              fetchParams: mediumParams,
              widget,
            },
          ),
        ),
      );
    } catch (error) {
      yield put(actions.fetchCampaignsError(error, widget));
    }
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Fetch Dashboard Saga                                                                           */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchDashboard
 * Saga for fetching dashboard.
 * @return { void }
 */
export function* fetchDashboard() {
  try {
    const response = yield call(fetchDashboardAPI);
    const { defaultStartRange: startRange, defaultEndRange: endRange } =
      yield select(getDashboardState);

    const { widgets, settings } = response.data;
    const dashboardData = formatWidgetDataResponse(widgets, settings, startRange, endRange);

    yield put(actions.fetchDashboardResponse(dashboardData));
  } catch (error) {
    yield put(actions.fetchDashboardError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Fetch Dashboard Data Sagas                                                                     */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchDashboardDataForWidget
 * Fetches data for a single widget (metric / visualization).
 * @param { Object } widget: The widget config to fetch data for.
 * @return { void }
 */
export function* fetchDashboardDataForWidget(widget) {
  try {
    const { startDate, endDate } = yield select(getDashboardState);
    const dataType = getWidgetDataType(widget);
    const metric = getWidgetMetric(widget);
    const timeParams = {
      startTime: formatDateForSave(startDate),
      endTime: formatDateForSave(endDate),
    };

    const response = yield call(fetchDashboardDataAPI, { dataType, metric, ...timeParams });
    const { dashboardData } = yield select(getDashboardState);
    yield put(
      actions.fetchDashboardDataResponse(
        deepMergeNew(dashboardData, createDashboardDataObject(widget, response.data)),
      ),
    );
    for (let i = 0; i < response?.data?.warnings?.length; i += 1) {
      const warning = response?.data?.warnings[i];
      yield addWarningToast(warning);
    }
  } catch (error) {
    yield put(actions.fetchDashboardDataWidgetError(error));
  }
}

/**
 * fetchDashboardData
 * Fetches data for each widget. Prevents fetching multiple widgets
 * of the same metric and visualization.
 * @returns { Object } Fetched data for given metric and visualization.
 */
export function* fetchDashboardData() {
  try {
    const { widgets } = yield select(getDashboardState);
    const data = {};

    /** Creates an empty data object for each metric and chart type. */
    const emptyDashboardData = Object.keys(widgets)
      .map((key) => widgets[key])
      .reduce((obj, widget) => {
        const newObj = obj;
        newObj[getWidgetDataType(widget)] = { [getWidgetDataIdentifier(widget)]: null };
        return newObj;
      }, {});
    yield put(actions.fetchDashboardDataInit(emptyDashboardData));

    yield all(
      Object.keys(widgets).map((id) => {
        const widget = widgets[id];
        const widgetIdentifier = getWidgetDataIdentifier(widget);
        const dataType = getWidgetDataType(widget);

        if (!data[dataType]) {
          data[dataType] = {};
        }
        if (isCampaignsTableWidget(widget)) {
          return call(fetchCampaigns, { widget });
        }
        /** Prevent calls to the same metric and visualization */
        if (isMetricWidget(widget) && !data[dataType][widgetIdentifier]) {
          data[dataType][widgetIdentifier] = true;
          return call(fetchDashboardDataForWidget, widget);
        }
        return undefined;
      }),
    );
    yield put(actions.fetchDashboardDataComplete());
  } catch (error) {
    yield put(actions.fetchDashboardDataError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Save Dashboard Saga                                                                            */
/*------------------------------------------------------------------------------------------------*/
/**
 * saveDashboard
 * Saga for saving dashboard.
 * @return { void }
 */
export function* saveDashboard() {
  try {
    const { campaignFetchParamData, defaultStartRange, defaultEndRange, layout, theme, widgets } =
      yield select(getDashboardState);

    const dashboardData = {
      widgets: createWidgetData(layout, widgets, campaignFetchParamData),
      settings: { defaultStartRange, defaultEndRange, theme },
    };
    const response = yield call(saveDashboardAPI, dashboardData);

    yield put(
      actions.saveDashboardResponse({
        response,
        ...dashboardData,
      }),
    );
  } catch (error) {
    yield put(actions.saveDashboardError(error));
  }
}

/*------------------------------------------------------------------------------------------------*/
/* Dashboard Widget Action Sagas                                                                  */
/*------------------------------------------------------------------------------------------------*/
export function* addWidget({ widgetOptions }) {
  try {
    const id = generateId();
    const { layout, widgets } = yield select(getDashboardState);
    const newWidget = { ...widgetOptions, id, i: id };
    yield put(
      actions.addWidgetResponse({
        layout: [...layout, { i: id, ...widgetOptions }],
        widgets: { ...widgets, [id]: newWidget },
      }),
    );
    if (isCampaignsTableWidget(newWidget)) {
      yield call(fetchCampaigns, { widget: newWidget });
    }
    if (isMetricWidget(newWidget)) {
      yield call(fetchDashboardDataForWidget, newWidget);
    }
  } catch (error) {
    yield put(actions.addWidgetError(error));
  }
}

/**
 * deleteWidget
 * Saga for deleting widget.
 * @param { Object } params: Delete params containing deletedWidgetId (id of widget to delete).
 * @return { void }
 */
export function* deleteWidget({ deletedWidgetId }) {
  try {
    const { layout, widgets } = yield select(getDashboardState);
    yield put(
      actions.deleteWidgetResponse({
        layout: filterFromList(layout, 'i', deletedWidgetId),
        widgets: omitFromObject(Object.assign({}, widgets), deletedWidgetId),
      }),
    );
    yield put(actions.closeDeleteModal());
    yield call(saveDashboard); // Save dashboard after
  } catch (error) {
    yield put(actions.deleteWidgetError(error));
  }
}

export function* saveWidget({ widgetOptions }) {
  try {
    const { widgets } = yield select(getDashboardState);
    const { id, ...widget } = widgetOptions;
    const oldWidget = widgets[id];
    const updatedWidget = { i: id, id, ...widget };

    yield put(
      actions.saveWidgetResponse({
        widgets: { ...omitFromObject(Object.assign({}, widgets), id), [id]: updatedWidget },
      }),
    );
    yield call(saveDashboard); // Save dashboard after

    if (hasMetricWidgetChanged(oldWidget, updatedWidget)) {
      yield call(fetchDashboardData); // Fetch dashboard if metric changes
    }
  } catch (error) {
    yield put(actions.saveWidgetError(error));
  }
}

addToRootSaga([
  takeLatest(actionTypes.ADD_WIDGET, addWidget),
  takeLatest(actionTypes.SAVE_WIDGET, saveWidget),
  takeLatest(actionTypes.DELETE_WIDGET, deleteWidget),
  takeLatest(actionTypes.FETCH_DASHBOARD, fetchDashboard),
  takeLatest(actionTypes.FETCH_DASHBOARD_DATA, fetchDashboardData),
  takeLatest(actionTypes.SAVE_DASHBOARD, saveDashboard),
  takeLatest(actionTypes.FETCH_CAMPAIGNS, fetchCampaigns),
]);
